/**
 * Frontend Main Application - PadelPulse
 * Router and view manager with Stitch Design Integration
 * Handles authentication and navigation across the application
 */

import "./style.css";
import { apiService } from "./services/api";
import { HomeView } from "./views/HomeView";
import { LandingPageView } from "./views/LandingPageView";
import { LeagueDetailView } from "./views/LeagueDetailView";
import { RoundView } from "./views/RoundView";
import { PlayerListView } from "./views/PlayerListView";
import { PublicLeagueView } from "./views/PublicLeagueView";
import { PlatformAdminView } from "./views/PlatformAdminView";
import { OrganizerInsightsView } from "./views/OrganizerInsightsView";
import { ChangePasswordView } from "./views/ChangePasswordView";
import { AccountView } from "./views/AccountView";
import { PricingView } from "./views/PricingView";
import { PrivacyView, TermsView } from "./views/LegalView";
import { setRobotsNoindex } from "./utils/ui";
import { initI18n, onLocaleChange, t } from "./i18n/config";

// The signed-in user's role, persisted at login (the JWT is opaque to the
// client). Drives owner-only routing; absent/anything-else means organizer.
export const getUserRole = (): string | null => localStorage.getItem("userRole");
export const isOwner = (): boolean => getUserRole() === "owner";

// Set at login when the account is on a temporary password. While true, the
// router forces the fullscreen password-reset view and blocks the dashboard.
export const mustChangePassword = (): boolean =>
  localStorage.getItem("mustChangePassword") === "true";

export const appContainer = document.getElementById("app");

if (!appContainer) {
  throw new Error("App container not found");
}

/**
 * Active visual skin, controlled by a single server-side / build-time property.
 * This is an ADMINISTRATIVE switch — there is no end-user toggle UI. The system
 * admin sets `VITE_THEME` ("dark" | "daylight") in the deployment environment
 * (see .env). Anything other than "daylight" falls back to the original night
 * theme, so existing deployments keep the "Power Navy & Pulse Lime" skin.
 */
export type AppTheme = "dark" | "daylight";

export function getConfiguredTheme(): AppTheme {
  const configured = (import.meta.env.VITE_THEME ?? "").toString().trim().toLowerCase();
  return configured === "daylight" ? "daylight" : "dark";
}

// Apply the admin-selected skin as a global class on the root <html> element.
export function setupTheme() {
  const theme = getConfiguredTheme();
  const root = document.documentElement;
  // Mutually exclusive: only one skin class is ever present on the root.
  root.classList.remove("dark", "daylight");
  root.classList.add(theme);
}

// Router based on hash navigation
export const router = async () => {
  const hash = window.location.hash.slice(1) || "landing";

  // Public, no-login league view reached via an unguessable sharing token:
  // "#/leagues/<token>". Checked BEFORE the auth gate so a shared WhatsApp link
  // works for anyone, and noindex'd so search engines never index it.
  const publicMatch = hash.match(/^\/leagues\/([^/]+)$/);
  setRobotsNoindex(!!publicMatch);
  if (publicMatch) {
    const publicView = new PublicLeagueView(appContainer, decodeURIComponent(publicMatch[1]));
    await publicView.render();
    return;
  }

  // Public marketing and legal routes. Checked BEFORE the auth gate so anyone
  // (logged out or in) can reach them, and left indexable for SEO — they carry
  // no private data. Owners/organizers can still open them via footer links.
  if (hash === "pricing") {
    const pricingView = new PricingView(appContainer);
    await pricingView.render();
    return;
  }
  if (hash === "privacy") {
    const privacyView = new PrivacyView(appContainer);
    await privacyView.render();
    return;
  }
  if (hash === "terms") {
    const termsView = new TermsView(appContainer);
    await termsView.render();
    return;
  }

  const token = localStorage.getItem("authToken");

  // Unauthenticated routes
  if (!token) {
    if (hash === "login") {
      showLandingPageView();
      new LoginModal().show();
      // The login modal is a transient overlay, not a real route. Reset the
      // hash so it isn't "sticky": otherwise it stays "#login", which means a
      // reload re-opens the modal and clicking "Login" again is a no-op (no
      // hashchange fires when the hash is already "#login"). Mirrors the
      // "#home/new" deep-link reset below.
      history.replaceState(null, "", "#landing");
    } else {
      showLandingPageView();
    }
    return;
  }

  // Forced first-login password reset. An organizer provisioned with a
  // temporary password must choose a permanent one before reaching any
  // authenticated view. Checked AFTER the auth gate (they're logged in) but
  // BEFORE every dashboard route, so there's no way around it. Owners are never
  // flagged, so this never traps them.
  if (mustChangePassword()) {
    const changePasswordView = new ChangePasswordView(appContainer);
    await changePasswordView.render();
    return;
  }

  // Account self-service is available to every signed-in user (owner and
  // organizer alike), so it's handled before the role-specific routing below.
  if (hash === "account") {
    const accountView = new AccountView(appContainer);
    await accountView.render();
    return;
  }

  // Platform owner routes. The owner has no tenant, so the organizer HomeView
  // is meaningless to them: their home IS the Platform Administration view.
  if (isOwner()) {
    if (hash === "admin" || hash === "home" || hash === "") {
      const adminView = new PlatformAdminView(appContainer);
      await adminView.render();
      return;
    }
    // The organizer Insights view is meaningless for the owner (it queries their
    // non-existent tenant); their analytics live inside the admin view.
    if (hash === "insights") {
      history.replaceState(null, "", "#admin");
      const adminView = new PlatformAdminView(appContainer);
      await adminView.render();
      return;
    }
  } else if (hash === "admin") {
    // Non-owners can't reach the admin view; send them home.
    history.replaceState(null, "", "#home");
    const homeView = new HomeView(appContainer);
    await homeView.render();
    return;
  }

  // Authenticated routes
  switch (true) {
    case hash === "home" || hash === "" || hash === "home/new":
      const homeView = new HomeView(appContainer);
      await homeView.render();
      // "#home/new" deep-links to the create-league form (used by the "New
      // League" button on other pages). Reset the hash first so a refresh or
      // closing the modal doesn't re-trigger it.
      if (hash === "home/new") {
        history.replaceState(null, "", "#home");
        await homeView.promptCreateLeague();
      }
      break;

    case hash.startsWith("players"):
      const playerView = new PlayerListView(appContainer);
      await playerView.render();
      break;

    case hash.startsWith("insights"):
      const insightsView = new OrganizerInsightsView(appContainer);
      await insightsView.render();
      break;

    case hash.startsWith("league/"):
      const leagueIdMatch = hash.match(/league\/(\d+)/);
      const leagueId = leagueIdMatch ? parseInt(leagueIdMatch[1]) : 0;

      if (hash.includes("/rounds/")) {
        const roundMatch = hash.match(/rounds\/(\d+)/);
        const roundNumber = roundMatch ? parseInt(roundMatch[1]) : 1;
        const roundView = new RoundView(appContainer, leagueId, roundNumber);
        await roundView.render();
      } else {
        const detailView = new LeagueDetailView(appContainer, leagueId);
        await detailView.render();
      }
      break;

    default:
      const defaultView = new HomeView(appContainer);
      await defaultView.render();
  }
};

// Landing page for unauthenticated users.
// The Login control now lives inside the landing page's own header
// (LandingPageView), so the router no longer injects a floating button here.
export const showLandingPageView = () => {
  const landingView = new LandingPageView(appContainer);
  landingView.render().catch(error => {
    console.error("[Landing] Failed to render:", error);
    showErrorView("Failed to load landing page");
  });
};

// Login Modal Class
export class LoginModal {
  private modal: HTMLElement;

  constructor() {
    this.modal = document.createElement("div");
    this.modal.className = "fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200";
    this.modal.innerHTML = `
      <div class="relative w-full max-w-md bg-white dark:bg-card-dark rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 p-8 md:p-10 animate-in zoom-in-95 duration-200">
        <!-- Close Button -->
        <button id="close-modal-btn" class="absolute top-4 right-4 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors" aria-label="${t("login.close")}">
          <span class="material-symbols-outlined">close</span>
        </button>

        <!-- Header -->
        <div class="text-center mb-8">
          <img src="/brand/icon-reverse.svg" alt="PadelPulse" class="size-16 mx-auto mb-4 hidden dark:block" />
          <img src="/brand/icon-color.svg" alt="PadelPulse" class="size-16 mx-auto mb-4 hidden daylight:block" />
          <h1 class="text-3xl md:text-4xl text-slate-900 dark:text-white mb-2"><span class="font-medium">PADEL</span><span class="font-extrabold">PULSE</span></h1>
          <p class="text-slate-600 dark:text-slate-400">${t("login.subtitle")}</p>
        </div>

        <!-- Login Form -->
        <form id="modal-login-form" class="space-y-5">
          <!-- Email Input -->
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
              <span class="material-symbols-outlined text-base align-middle mr-2">mail</span>
              ${t("login.email")}
            </label>
            <input
              type="email"
              id="modal-email"
              required
              class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition"
              placeholder="${t("login.emailPlaceholder")}"
            />
          </div>

          <!-- Password Input -->
          <div>
            <label class="block text-sm font-semibold text-slate-900 dark:text-white mb-2">
              <span class="material-symbols-outlined text-base align-middle mr-2">lock</span>
              ${t("login.password")}
            </label>
            <input
              type="password"
              id="modal-password"
              required
              class="w-full px-4 py-3 border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-800 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent transition"
              placeholder="••••••••"
            />
          </div>

          <!-- Remember Me -->
          <div class="flex items-center justify-between text-sm">
            <label class="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" class="rounded border-slate-300 dark:border-slate-700" />
              <span class="text-slate-600 dark:text-slate-400">${t("login.remember")}</span>
            </label>
            <a href="#" class="text-primary hover:text-primary/80 font-medium">${t("login.forgot")}</a>
          </div>

          <!-- Error Message -->
          <div id="modal-error-message" class="hidden bg-error/10 border border-error/30 text-error rounded-lg p-4 text-sm font-medium flex items-start gap-3">
            <span class="material-symbols-outlined flex-shrink-0">error</span>
            <span id="modal-error-text">${t("login.error")}</span>
          </div>

          <!-- Submit Button -->
          <button
            type="submit"
            class="w-full bg-gradient-to-r from-primary to-blue-600 hover:from-primary/90 hover:to-blue-600/90 text-white font-bold py-3 px-4 rounded-lg shadow-lg shadow-primary/30 transition-all active:scale-95 flex items-center justify-center gap-2"
          >
            <span class="material-symbols-outlined">login</span>
            <span>${t("login.submit")}</span>
          </button>
        </form>

        <!-- Divider -->
        <div class="my-6 flex items-center gap-3">
          <div class="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
          <span class="text-xs text-slate-500 dark:text-slate-400 font-medium">${t("login.or")}</span>
          <div class="flex-1 h-px bg-slate-200 dark:bg-slate-700"></div>
        </div>

        <!-- Demo Info -->
        <div class="bg-accent/10 dark:bg-accent/5 border border-accent/30 rounded-lg p-4 text-center">
          <p class="text-sm text-slate-700 dark:text-slate-300">
            <span class="font-semibold">${t("login.demoLabel")}</span> ${t("login.demoText")}
          </p>
        </div>
      </div>
    `;

    this.setupListeners();
  }

  private setupListeners() {
    const form = this.modal.querySelector("#modal-login-form") as HTMLFormElement;
    const closeBtn = this.modal.querySelector("#close-modal-btn") as HTMLButtonElement;
    const errorDiv = this.modal.querySelector("#modal-error-message") as HTMLElement;
    const errorText = this.modal.querySelector("#modal-error-text") as HTMLElement;

    closeBtn.addEventListener("click", () => this.close());
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      
      const email = (this.modal.querySelector("#modal-email") as HTMLInputElement).value;
      const password = (this.modal.querySelector("#modal-password") as HTMLInputElement).value;

      if (errorDiv) errorDiv.classList.add("hidden");

      try {
        const response = await apiService.login(email, password);

        if (response.token) {
          localStorage.setItem("authToken", response.token);
          // Persist the role so the router can gate the owner-only admin view.
          const role = response.user?.role;
          if (role) localStorage.setItem("userRole", role);
          else localStorage.removeItem("userRole");
          // Persist the forced-reset gate. When set, the router intercepts every
          // authenticated route with the mandatory password-change view.
          if (response.user?.mustChangePassword) {
            localStorage.setItem("mustChangePassword", "true");
          } else {
            localStorage.removeItem("mustChangePassword");
          }
          this.close();
          // Owners land on the Platform Administration view; organizers on home.
          // (A forced-reset organizer is redirected by the router regardless.)
          window.location.hash = role === "owner" ? "#admin" : "#home";
          router();
        } else {
          throw new Error(response.error || t("login.authFailed"));
        }
      } catch (error) {
        console.error("[Login] Error:", error);
        if (errorDiv && errorText) {
          errorText.textContent = error instanceof Error ? error.message : t("login.error");
          errorDiv.classList.remove("hidden");
        }
      }
    });
  }

  show() {
    document.body.appendChild(this.modal);
  }

  close() {
    this.modal.remove();
  }
}

// Error view for critical failures
export const showErrorView = (message: string) => {
  appContainer.innerHTML = `
    <div class="min-h-screen bg-gradient-to-br from-background-light to-slate-100 dark:from-background-dark dark:to-slate-900 flex items-center justify-center p-4">
      <div class="text-center max-w-md">
        <div class="mb-6">
          <span class="material-symbols-outlined text-6xl text-error">error_outline</span>
        </div>
        <h1 class="text-3xl font-black text-slate-900 dark:text-white mb-3">Something went wrong</h1>
        <p class="text-slate-600 dark:text-slate-400 mb-8">${message}</p>
        <button
          onclick="window.location.reload()"
          class="bg-primary hover:bg-primary/90 text-navy-800 font-bold py-2 px-6 rounded-lg transition flex items-center justify-center gap-2 mx-auto"
        >
          <span class="material-symbols-outlined">refresh</span>
          <span>Retry</span>
        </button>
      </div>
    </div>
  `;
};

// Initialize application
setupTheme();
initI18n();
window.addEventListener("hashchange", router);
// Re-render the active view when the language changes so localized strings swap
// in instantly, without a full page reload.
onLocaleChange(() => {
  router();
});
router();